import { getLudDocumentFileUrlAPI } from "../../../utils/api/ludDocuments";
import { APPS, MONTH_NAMES } from "./ludDocuments.constants";
import { normalizeDeliveryAppKey } from "../../../utils/integrations";

export const getText = (source, key, lang = "ar") =>
  source[key]?.[lang] || source[key]?.ar || key;

export const formatDate = (iso, lang = "ar") => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const months = MONTH_NAMES[lang] || MONTH_NAMES.ar;
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

export const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date();
  return Math.round((new Date(iso) - today) / 86400000);
};

const toDateOnly = (value) => value ? String(value).slice(0, 10) : null;

const groupByApp = (items = []) =>
  items.reduce((result, item) => {
    if (!item?.app) return result;
    result[item.app] = [...(result[item.app] || []), item];
    return result;
  }, {});

// Records written before the canonical vocabulary use "hunger" for
// HungerStation; every app reference is read as its canonical key.
const documentAppKey = (app) => normalizeDeliveryAppKey(app) || app;
const withCanonicalApps = (source) => {
  if (!source) return source;
  const withApp = (items) =>
    Array.isArray(items) ? items.map((item) => (item?.app ? { ...item, app: documentAppKey(item.app) } : item)) : items;
  return {
    ...source,
    enabledApps: Array.isArray(source.enabledApps) ? source.enabledApps.map(documentAppKey) : source.enabledApps,
    appStatus: source.appStatus
      ? Object.fromEntries(Object.entries(source.appStatus).map(([app, status]) => [documentAppKey(app), status]))
      : source.appStatus,
    reportFiles: withApp(source.reportFiles),
    contracts: withApp(source.contracts),
    statements: withApp(source.statements),
  };
};

export const mapLudRecordToRestaurant = (rawSummary = {}, rawRecord = null) => {
  const summary = withCanonicalApps(rawSummary) || {};
  const record = withCanonicalApps(rawRecord);
  const configuredApps = record?.enabledApps || summary.enabledApps || [];
  const statusApps = Object.keys(record?.appStatus || summary.appStatus || {});
  const enabledApps = configuredApps.length
    ? configuredApps
    : statusApps.length
      ? statusApps
      : Object.keys(APPS);
  const appStatus = record?.appStatus || summary.appStatus || {};
  const organizationDocuments = record?.organizationDocuments || [];
  const reportFiles = (record?.reportFiles || []).map((report) => ({
    ...report,
    id: report._id || report.id,
  }));
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const orgDocs = organizationDocuments
    .filter((document) => ["cr", "iban", "tax", "license", "address"].includes(document.key))
    .map((document) => ({
      key: document.key,
      nameAr: document.titleAr,
      nameEn: document.titleEn,
      essential: document.essential,
      addedDate: toDateOnly(document.currentFile?.uploadedAt || document.updatedAt),
      expiryDate: toDateOnly(document.expiryDate),
      history: document.history || [],
      currentFile: document.currentFile || null,
    }));

  const specialDocument = (key) => organizationDocuments.find((document) => document.key === key);
  const productPhotos = specialDocument("productPhotos");
  const logo = specialDocument("logo");
  const menuInfo = specialDocument("menuInfo");
  const restaurantMenu = specialDocument("restaurantMenu");
  const deliveryAppsMenu = specialDocument("deliveryAppsMenu");
  const foodCostSheet = specialDocument("foodCostSheet");

  const contractsByApp = (record?.contracts || []).reduce((result, contract) => {
    if (!contract?.app) return result;

    if (!result[contract.app]) {
      result[contract.app] = [];
    }

    result[contract.app].push(contract);
    return result;
  }, {});

  const contracts = Object.entries(contractsByApp).reduce(
    (result, [app, appContracts]) => {
      const sortedContracts = appContracts
        .slice()
        .sort((first, second) => {
          const firstDate = new Date(
            first.updatedAt ||
              first.createdAt ||
              first.startDate ||
              0,
          ).getTime();
          const secondDate = new Date(
            second.updatedAt ||
              second.createdAt ||
              second.startDate ||
              0,
          ).getTime();

          return secondDate - firstDate;
        });

      const selectedContract =
        sortedContracts.find((contract) => contract?.currentFile) ||
        sortedContracts[0];

      if (!selectedContract) {
        return result;
      }

      const history = sortedContracts
        .flatMap((contract) =>
          (contract.history || []).map((entry) => ({
            ...entry,
            contractId: contract._id || contract.id,
          })),
        )
        .sort(
          (first, second) =>
            new Date(second.archivedAt || 0).getTime() -
            new Date(first.archivedAt || 0).getTime(),
        );

      result[app] = {
        ...selectedContract,
        id: selectedContract._id || selectedContract.id,
        startDate: toDateOnly(selectedContract.startDate),
        endDate: toDateOnly(selectedContract.endDate),
        history,
      };

      return result;
    },
    {},
  );

  const statements = groupByApp(record?.statements || []);
  Object.keys(statements).forEach((app) => {
    statements[app] = statements[app].map((statement) => ({
      ...statement,
      id: statement._id,
      from: toDateOnly(statement.from),
      to: toDateOnly(statement.to),
      addedDate: toDateOnly(statement.createdAt),
    }));
  });

  const updates = (record?.updateRequests || []).map((request) => ({
    ...request,
    id: request._id,
    date: toDateOnly(request.createdAt),
  }));

  const dailyReports = reportFiles.filter((report) => report.type === "daily");
  const monthlyReports = reportFiles.filter((report) => report.type === "monthly");
  const itemSalesReports = reportFiles.filter((report) => report.type === "itemSales");

  const dailyCoverage = {};
  dailyReports.forEach((report) => {
    const previous = dailyCoverage[report.app];
    if (!previous || new Date(report.updatedAt || report.createdAt) > new Date(previous.updatedAt || 0)) {
      dailyCoverage[report.app] = {
        from: toDateOnly(report.coverageFrom),
        to: toDateOnly(report.coverageTo),
        updatedAt: report.updatedAt || report.createdAt,
      };
    }
  });

  const monthlyCoverage = {};
  groupByApp(monthlyReports);
  enabledApps.forEach((app) => {
    const appReports = monthlyReports.filter((report) => report.app === app);
    const months = [...new Set(appReports.map((report) => `${report.year}-${report.month}`))].sort();
    if (months.length) {
      const first = appReports.slice().sort((a,b) => a.year-b.year || a.month-b.month)[0];
      const last = appReports.slice().sort((a,b) => b.year-a.year || b.month-a.month)[0];
      monthlyCoverage[app] = {
        monthsCount: months.length,
        from: MONTH_NAMES.ar[(first.month || 1) - 1],
        to: MONTH_NAMES.ar[(last.month || 1) - 1],
      };
    }
  });

  const activeApps = enabledApps.filter((app) => {
    const status = appStatus?.[app] || "active";
    return !["closed", "closed_temp", "inactive"].includes(status);
  });

  const dailyMissing = activeApps.filter((app) => !dailyReports.some((report) => report.app === app && Number(report.year) === currentYear && Number(report.month) === currentMonth));
  const monthlyMissing = activeApps.filter((app) => !monthlyReports.some((report) => report.app === app && Number(report.year) === currentYear && Number(report.month) === currentMonth));
  const itemSalesMissing = activeApps.filter((app) => {
    const periods = itemSalesReports
      .filter((report) => report.app === app && Number(report.year) === currentYear && Number(report.month) === currentMonth)
      .map((report) => report.period);
    return !(periods.includes("period1") && periods.includes("period2"));
  });

  const essentialMissing = organizationDocuments
    .filter((document) => document.essential && !document.currentFile)
    .map((document) => document.titleAr);
  const secondaryMissing = organizationDocuments
    .filter((document) => !document.essential && !document.currentFile)
    .map((document) => document.titleAr);

  return {
    ...summary,
    id: summary.id || record?.storeCode || "",
    mongoId: summary.mongoId || record?.store?._id || record?.store || null,
    name: summary.name || record?.store?.businessName || record?.store?.name || "",
    nameEn: summary.nameEn || record?.store?.moreDetails?.businessNameEn || summary.name || "",
    plan: summary.plan || record?.store?.details?.subscriptionType || null,
    enabledApps,
    appStatus,
    hasItemSales: record?.hasItemSales ?? summary.hasItemSales ?? false,
    orgDocs,
    productPhotosAdded: toDateOnly(productPhotos?.currentFile?.uploadedAt || productPhotos?.updatedAt),
    productPhotosFile: productPhotos?.currentFile || null,
    productPhotosHistory: productPhotos?.history || [],
    logoAdded: toDateOnly(logo?.currentFile?.uploadedAt || logo?.updatedAt),
    logoFile: logo?.currentFile || null,
    logoHistory: logo?.history || [],
    menuInfoAdded: toDateOnly(
      restaurantMenu?.currentFile?.uploadedAt ||
      deliveryAppsMenu?.currentFile?.uploadedAt ||
      foodCostSheet?.currentFile?.uploadedAt ||
      menuInfo?.currentFile?.uploadedAt ||
      restaurantMenu?.updatedAt ||
      deliveryAppsMenu?.updatedAt ||
      foodCostSheet?.updatedAt ||
      menuInfo?.updatedAt,
    ),
    menuInfoFile: restaurantMenu?.currentFile || menuInfo?.currentFile || null,
    menuInfoHistory: restaurantMenu?.history?.length
      ? restaurantMenu.history
      : menuInfo?.history || [],
    restaurantMenuFile: restaurantMenu?.currentFile || menuInfo?.currentFile || null,
    restaurantMenuReference: restaurantMenu?.currentFile
      ? "restaurantMenu"
      : menuInfo?.currentFile
        ? "menuInfo"
        : "restaurantMenu",
    restaurantMenuHistory: restaurantMenu?.history?.length
      ? restaurantMenu.history
      : menuInfo?.history || [],
    restaurantMenuHistoryReference: restaurantMenu?.history?.length
      ? "restaurantMenu"
      : menuInfo?.history?.length
        ? "menuInfo"
        : "restaurantMenu",
    deliveryAppsMenuFile: deliveryAppsMenu?.currentFile || null,
    deliveryAppsMenuHistory: deliveryAppsMenu?.history || [],
    foodCostSheetFile: foodCostSheet?.currentFile || null,
    foodCostSheetHistory: foodCostSheet?.history || [],
    contracts,
    statements,
    updates,
    reportFiles,
    dailyCoverage,
    monthlyCoverage,
    dailyMissing,
    monthlyMissing,
    itemSalesMissing,
    essentialMissing,
    secondaryMissing,
  };
};


export const restaurantHasIssues = (r) => {
  const cr = r.orgDocs.find((d) => d.key === "cr");
  return Boolean(
    r.essentialMissing.length ||
      (r.plan !== "L" && r.dailyMissing.length) ||
      r.monthlyMissing.length ||
      (r.hasItemSales && r.itemSalesMissing.length) ||
      r.updates.some((u) => ["new", "in_progress"].includes(u.status)) ||
      (cr?.expiryDate && daysUntil(cr.expiryDate) <= 30),
  );
};

export const categoryHasIssue = (key, r) => {
  if (key === "orgdocs") return r.essentialMissing.length > 0;
  if (key === "daily") return r.plan !== "L" && r.dailyMissing.length > 0;
  if (key === "monthly") return r.monthlyMissing.length > 0;
  if (key === "itemsales") return r.hasItemSales && r.itemSalesMissing.length > 0;
  if (key === "updates") return r.updates.some((u) => ["new", "in_progress"].includes(u.status));
  return false;
};

export const buildAlerts = (r, lang) => {
  const ar = lang === "ar";
  const alerts = [];
  if (r.plan !== "L") {
    r.dailyMissing.forEach((key) =>
      alerts.push({
        level: "danger",
        text: ar
          ? `${APPS[key].label[lang]} — التقرير اليومي لم يتحدث اليوم`
          : `${APPS[key].label[lang]} — the daily report was not updated today`,
        when: ar ? "يتطلب متابعة فورية" : "Requires immediate follow-up",
      }),
    );
  }
  // Monthly files are expected by the 9th of each month.
  if (new Date().getDate() >= 9) {
    r.monthlyMissing.forEach((key) =>
      alerts.push({
        level: "warning",
        text: ar
          ? `${APPS[key].label[lang]} — ملفات التقرير الشهري لم تُرفع بعد`
          : `${APPS[key].label[lang]} — monthly report files have not been uploaded`,
        when: ar ? "المتوقع رفعها كل يوم 9 من الشهر" : "Expected by the 9th of each month",
      }),
    );
  }
  if (r.hasItemSales) {
    r.itemSalesMissing.forEach((key) =>
      alerts.push({
        level: "warning",
        text: ar
          ? `${APPS[key].label[lang]} — فترة مبيعات أصناف ناقصة`
          : `${APPS[key].label[lang]} — an item-sales period is missing`,
        when: ar ? "المتوقع رفعها كل يوم 10 و 25" : "Expected on the 10th and 25th",
      }),
    );
  }
  return alerts;
};

export const getCategoryView = (key, r, lang) => {
  const ar = lang === "ar";
  const common = {
    alerts: ["daily", "monthly", "itemsales"].includes(key)
      ? buildAlerts(r, lang)
      : [],
  };

  if (key === "orgdocs") {
    const essentialDocs = r.orgDocs.filter((d) => d.essential);
    const secondaryDocs = r.orgDocs.filter((d) => !d.essential);
    const essentialDone = essentialDocs.filter((d) => d.addedDate).length;
    const secondaryDone = secondaryDocs.filter((d) => d.addedDate).length;
    const completed = essentialDone + secondaryDone + (r.productPhotosAdded ? 1 : 0) + 2;
    return {
      ...common,
      count: ar
        ? `${completed} / ${r.orgDocs.length + 3} عنصر مكتمل`
        : `${completed} / ${r.orgDocs.length + 3} completed`,
      metrics: [
        {
          label: ar ? "المستندات الأساسية" : "Essential documents",
          value: `${essentialDone} / ${essentialDocs.length}`,
          note: r.essentialMissing.length
            ? `${ar ? "ناقص" : "Missing"}: ${r.essentialMissing.join(ar ? "، " : ", ")}`
            : ar ? "مكتملة بالكامل" : "Fully complete",
        },
        {
          label: ar ? "المستندات الثانوية" : "Secondary documents",
          value: `${secondaryDone} / ${secondaryDocs.length}`,
          note: r.secondaryMissing.filter((x) => x !== "صور المنتجات").length
            ? `${ar ? "ناقص" : "Missing"}: ${r.secondaryMissing.filter((x) => x !== "صور المنتجات").join(ar ? "، " : ", ")}`
            : ar ? "مكتملة بالكامل" : "Fully complete",
        },
        {
          label: ar ? "شعار المطعم" : "Restaurant logo",
          value: "✓",
          note: `${ar ? "أُضيف" : "Added"} ${formatDate(r.logoAdded, lang)}`,
        },
        {
          label: ar ? "معلومات المنيو" : "Menu information",
          value: "✓",
          note: `${ar ? "آخر تحديث" : "Last updated"} ${formatDate(r.menuInfoAdded, lang)}`,
        },
      ],
    };
  }

  if (key === "contracts") {
    const count = Object.keys(r.contracts).length;
    const starts = Object.values(r.contracts).map((c) => c.startDate).sort();
    return {
      ...common,
      count: ar ? `${count} عقد ساري` : `${count} active contracts`,
      metrics: [
        { label: ar ? "عقود سارية" : "Active contracts", value: count, note: ar ? "من إجمالي تطبيقات التوصيل" : "Of all delivery apps" },
        { label: ar ? "تطبيقات غير مفعّلة" : "Inactive applications", value: 6 - count, note: ar ? "لا تظهر بقية الأقسام لها" : "Other sections are hidden for them" },
        { label: ar ? "أقدم عقد" : "Oldest contract", value: starts[0] ? formatDate(starts[0], lang) : "—", note: ar ? "تاريخ البدء" : "Start date" },
        { label: ar ? "الحالة العامة" : "Overall status", value: ar ? "سارٍ" : "Active", note: ar ? "لا يوجد عقود منتهية" : "No expired contracts" },
      ],
    };
  }

  if (key === "statements") {
    const apps = Object.keys(r.statements);
    const total = apps.reduce((sum, k) => sum + r.statements[k].length, 0);
    return {
      ...common,
      count: total ? (ar ? `${total} كشف مضاف` : `${total} statements added`) : ar ? "لا توجد كشوفات" : "No statements",
      metrics: [
        { label: ar ? "كشوفات مضافة" : "Statements added", value: total, note: ar ? "عبر كل التطبيقات" : "Across all apps" },
        { label: ar ? "تطبيقات لها كشوفات" : "Apps with statements", value: apps.length, note: ar ? `من ${r.enabledApps.length} تطبيق مفعّل` : `Of ${r.enabledApps.length} enabled apps` },
        { label: ar ? "النوع" : "Type", value: "PDF", note: ar ? "مستند واحد لكل فترة" : "One document per period" },
        { label: ar ? "آلية الإضافة" : "Addition method", value: ar ? "عند الطلب" : "On request", note: ar ? "فقط إذا طلبها العميل" : "Only when requested by the client" },
      ],
    };
  }

  if (key === "updates") {
    const active = r.updates.filter((u) => ["new", "in_progress"].includes(u.status)).length;
    return {
      ...common,
      count: r.updates.length ? (ar ? `${r.updates.length} طلب` : `${r.updates.length} requests`) : ar ? "لا توجد طلبات" : "No requests",
      metrics: [
        { label: ar ? "إجمالي الطلبات" : "Total requests", value: r.updates.length, note: ar ? "منذ بداية التسجيل" : "Since registration" },
        { label: ar ? "قيد المعالجة" : "In progress", value: active, note: active ? (ar ? "يتطلب متابعة" : "Requires follow-up") : ar ? "لا يوجد نشط" : "No active requests" },
        { label: ar ? "مكتملة" : "Completed", value: r.updates.filter((u) => u.status === "done").length, note: ar ? "تم تنفيذها" : "Executed" },
        { label: ar ? "ملغاة" : "Cancelled", value: r.updates.filter((u) => u.status === "cancelled").length, note: ar ? "لم تُنفَّذ" : "Not executed" },
      ],
    };
  }

  if (key === "daily") {
    const isL = r.plan === "L";
    const ok = r.enabledApps.length - r.dailyMissing.length;
    return {
      ...common,
      count: isL ? (ar ? "باقة L — تحديث غير دوري" : "Plan L — non-periodic update") : ar ? `${ok} / ${r.enabledApps.length} محدّث اليوم` : `${ok} / ${r.enabledApps.length} updated today`,
      metrics: [
        { label: ar ? "التطبيقات المحدّثة اليوم" : "Apps updated today", value: isL ? "—" : `${ok} / ${r.enabledApps.length}`, note: isL ? (ar ? "الباقة L لا تدعم التحديث اليومي" : "Plan L does not support daily updates") : ar ? "حسب آخر تحديث" : "According to the last update" },
        { label: ar ? "نوع الباقة" : "Plan type", value: isL ? "L" : ar ? "قياسية" : "Standard", note: isL ? (ar ? "يُضاف مرة واحدة فقط" : "Added once only") : ar ? "تحديث يومي تلقائي" : "Automatic daily update" },
        { label: ar ? "آخر تحديث" : "Last update", value: isL ? (ar ? "ثابت" : "Static") : ar ? "اليوم" : "Today", note: isL ? (ar ? "منذ الاشتراك" : "Since subscription") : ar ? "الساعة 09:15 صباحاً" : "09:15 AM" },
        { label: ar ? "تطبيقات متأخرة" : "Delayed apps", value: isL ? 0 : r.dailyMissing.length, note: r.dailyMissing.length ? (ar ? "تحتاج متابعة" : "Need follow-up") : ar ? "لا يوجد تأخر" : "No delay" },
      ],
    };
  }

  if (key === "monthly") {
    const ok = r.enabledApps.length - r.monthlyMissing.length;
    return {
      ...common,
      count: ar ? `${ok} / ${r.enabledApps.length} مكتمل هذا الشهر` : `${ok} / ${r.enabledApps.length} complete this month`,
      metrics: [
        { label: ar ? "مكتملة هذا الشهر" : "Completed this month", value: `${ok} / ${r.enabledApps.length}`, note: ar ? "يونيو 2026" : "June 2026" },
        { label: ar ? "موعد المتابعة" : "Follow-up date", value: ar ? "يوم 9" : "Day 9", note: ar ? "من كل شهر" : "Of every month" },
        { label: ar ? "الشهر الحالي" : "Current month", value: ar ? "لم يبدأ" : "Not started", note: ar ? "قيد الانتظار" : "Pending" },
        { label: ar ? "ملفات متأخرة" : "Delayed files", value: r.monthlyMissing.length, note: r.monthlyMissing.length ? (ar ? "تحتاج متابعة" : "Need follow-up") : ar ? "لا يوجد تأخر" : "No delay" },
      ],
    };
  }

  // if (!r.hasItemSales) {
  //   return {
  //     ...common,
  //     count: ar ? "غير مفعّلة" : "Not enabled",
  //     metrics: [{ label: ar ? "الحالة" : "Status", value: ar ? "غير مفعّلة" : "Not enabled", note: ar ? "لهذا المطعم" : "For this restaurant" }],
  //   };
  // }

  const ok = r.enabledApps.length - r.itemSalesMissing.length;
  return {
    ...common,
    count: ar ? `${ok} / ${r.enabledApps.length} مكتمل` : `${ok} / ${r.enabledApps.length} complete`,
    metrics: [
      { label: ar ? "مكتملة" : "Completed", value: `${ok} / ${r.enabledApps.length}`, note: ar ? "يونيو 2026" : "June 2026" },
      { label: ar ? "مواعيد المتابعة" : "Follow-up dates", value: ar ? "10 و 25" : "10 and 25", note: ar ? "من كل شهر" : "Of every month" },
      { label: ar ? "فترات ناقصة" : "Missing periods", value: r.itemSalesMissing.length, note: r.itemSalesMissing.length ? (ar ? "تحتاج متابعة" : "Need follow-up") : ar ? "لا يوجد نقص" : "No missing periods" },
      { label: ar ? "عدد الفترات لكل تطبيق" : "Periods per app", value: 2, note: ar ? "١–٩ و ١٠–٢٤" : "1–9 and 10–24" },
    ],
  };
};

export const buildAdminIssues = (restaurants, lang) => {
  const ar = lang === "ar";
  const issues = [];
  restaurants.forEach((r) => {
    r.essentialMissing.forEach((name) => issues.push({ id: `${r.id}-org-${name}`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "orgdocs", category: ar ? "مستندات المؤسسة" : "Organization Documents", detail: ar ? `مستند أساسي ناقص: ${name}` : `Missing essential document: ${name}`, severity: "danger" }));
    if (r.plan !== "L") r.dailyMissing.forEach((key) => issues.push({ id: `${r.id}-daily-${key}`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "daily", category: ar ? "التقرير اليومي" : "Daily Report", detail: ar ? `${APPS[key].label[lang]} — لم يتحدث اليوم` : `${APPS[key].label[lang]} — not updated today`, severity: "danger" }));
    r.monthlyMissing.forEach((key) => issues.push({ id: `${r.id}-monthly-${key}`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "monthly", category: ar ? "التقرير الشهري" : "Monthly Report", detail: ar ? `${APPS[key].label[lang]} — ملفات الشهر لم تُرفع بعد يوم 9` : `${APPS[key].label[lang]} — monthly files were not uploaded after day 9`, severity: "warning" }));
    if (r.hasItemSales) r.itemSalesMissing.forEach((key) => issues.push({ id: `${r.id}-items-${key}`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "itemsales", category: ar ? "مبيعات الأصناف" : "Item Sales", detail: ar ? `${APPS[key].label[lang]} — فترة ناقصة` : `${APPS[key].label[lang]} — missing period`, severity: "warning" }));
    r.updates.filter((u) => ["new", "in_progress"].includes(u.status)).forEach((u) => issues.push({ id: `${r.id}-update-${u.id}`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "updates", category: ar ? "تحديثات" : "Updates", detail: ar ? `طلب ${u.type === "iban" ? "آيبان بنكي" : u.type === "transfer" ? "نقل ملكية" : "بيانات تواصل"} — ${u.status === "new" ? "جديد" : "جاري العمل"}` : "Pending update request", severity: u.status === "new" ? "warning" : "neutral" }));
    const cr = r.orgDocs.find((d) => d.key === "cr");
    if (cr?.expiryDate) {
      const days = daysUntil(cr.expiryDate);
      if (days < 0) issues.push({ id: `${r.id}-cr-expired`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "orgdocs", category: ar ? "مستندات المؤسسة" : "Organization Documents", detail: ar ? "السجل التجاري منتهي الصلاحية" : "Commercial registration expired", severity: "danger" });
      else if (days <= 30) issues.push({ id: `${r.id}-cr-expiring`, restaurantId: r.id, restaurant: r.name, restaurantCode: r.id, categoryKey: "orgdocs", category: ar ? "مستندات المؤسسة" : "Organization Documents", detail: ar ? `السجل التجاري ينتهي خلال ${days} يوم` : `Commercial registration expires in ${days} days`, severity: "warning" });
    }
  });
  const order = { danger: 0, warning: 1, neutral: 2 };
  return issues.sort((a, b) => order[a.severity] - order[b.severity]);
};


const getLudFileExtension = (file, fallbackName = "") => {
  const fileName = String(
    fallbackName || file?.originalName || file?.name || "",
  ).trim();
  const lastDotIndex = fileName.lastIndexOf(".");

  if (lastDotIndex > -1 && lastDotIndex < fileName.length - 1) {
    return fileName.slice(lastDotIndex + 1).toLowerCase();
  }

  const mimeExtensions = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "application/pdf": "pdf",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "xlsx",
    "application/zip": "zip",
    "application/x-zip-compressed": "zip",
  };

  return mimeExtensions[String(file?.mimeType || "").toLowerCase()] || "";
};

export const getLudFileName = ({
  file,
  kind,
  reference,
  originalName,
}) => {
  const extension = getLudFileExtension(file, originalName);

  const organizationFileNames = {
    cr: "commercial-registration",
    iban: "iban-certificate",
    tax: "vat-certificate",
    vat: "vat-certificate",
    license: "business-license",
    address: "national-address",
    national_address: "national-address",
    productPhotos: "product-images",
    logo: "logo",
    menuInfo: "menu-information",
    restaurantMenu: "restaurant-menu",
    deliveryAppsMenu: "delivery-apps-menu",
    foodCostSheet: "food-cost-sheet",
  };

  const fallbackBaseName =
    file?.displayName ||
    file?.documentName ||
    file?.titleEn ||
    file?.titleAr ||
    file?.name ||
    "";

  let baseName = "";

  if (kind === "organizationDocument" || kind === "organization") {
    baseName = organizationFileNames[reference] || fallbackBaseName;
  } else if (kind === "contract") {
    baseName = file?.app
      ? `${file.app}-contract`
      : fallbackBaseName || "app-contract";
  } else if (kind === "statement") {
    baseName = file?.app
      ? `${file.app}-statement`
      : fallbackBaseName || "statement";
  } else if (kind === "report") {
    const reportTypeNames = {
      daily: "daily-report",
      monthly: "monthly-report",
      itemSales: "item-sales-report",
    };
    baseName =
      reportTypeNames[file?.type] ||
      fallbackBaseName ||
      "report";
  } else {
    baseName =
      organizationFileNames[reference] ||
      fallbackBaseName;
  }

  if (!baseName) {
    return originalName || file?.originalName || "file";
  }

  const normalizedBaseName = String(baseName)
    .trim()
    .replace(/\.[^/.]+$/, "");

  const storedName = String(
    originalName || file?.originalName || "",
  ).trim();
  const storedNameWithoutExtension = storedName.replace(/\.[^/.]+$/, "");

  const standardizedNumberedNamePattern =
    /^(?:commercial-registration|iban|tax-certificate|shop-license|national-address|product-photos|restaurant-logo|menu-information|restaurant-menu|delivery-apps-menu|food-cost-sheet|(?:keeta|hungerstation|hunger|jahez|marsool|ninja|toyou)-(?:contract|statement|.+)|(?:contact|iban|transfer)-update-request)-\d+$/i;

  if (
    storedName &&
    standardizedNumberedNamePattern.test(storedNameWithoutExtension)
  ) {
    return storedName;
  }

  return extension
    ? `${normalizedBaseName}.${extension}`
    : normalizedBaseName;
};

export const openLudFile = async (
  file,
  { token, storeId, kind, reference, historyId, onError } = {},
) => {
  if (!file) return;

  const mimeType = String(file.mimeType || "").toLowerCase();
  const fileKind = String(file.kind || "").toLowerCase();
  const canPreview =
    fileKind === "pdf" ||
    fileKind === "image" ||
    mimeType === "application/pdf" ||
    mimeType.startsWith("image/");

  try {
    let url = file.url;
    let originalName = file.originalName || file.name || "";

    if (token && storeId && kind && reference) {
      const response = await getLudDocumentFileUrlAPI(token, storeId, {
        kind,
        reference,
        historyId,
      });

      url = response?.url;
      originalName =
        response?.originalName ||
        originalName;
    }

    if (!url) throw new Error("The file link is not available");

    if (canPreview) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const downloadResponse = await fetch(url);

    if (!downloadResponse.ok) {
      throw new Error(
        `Failed to download file: ${downloadResponse.status}`,
      );
    }

    const blob = await downloadResponse.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = getLudFileName({
      file,
      kind,
      reference,
      originalName,
    });

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    // Callers pass onError so a failed preview/download is shown to the user.
    if (onError) onError(error);
    else console.error(error);
  }
};
